import { formatCompactINR } from "../../lib/format";
import { Gauge, Flame, CalendarClock } from "lucide-react";

function Bar({ label, value, max, color, sub }) {
  const pct = max ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1.5">
        <span className="text-slate-300 font-medium">{label}</span>
        <span className="text-slate-400 tabular">{sub}</span>
      </div>
      <div className="h-2.5 rounded-full bg-[#0F172A] overflow-hidden">
        <div className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

export default function BudgetCommandCenter({ summary, budget }) {
  if (!summary || !budget) return null;
  const pacing = summary.pacing_status;
  const pacingColor = pacing === "over" ? "#EF4444" : pacing === "under" ? "#F59E0B" : "#10B981";
  const burnLabel = pacing === "over" ? "Over-spending" : pacing === "under" ? "Under-spending" : "On Track";
  const pa = budget.platform_allocations || {};
  const docTotal = budget.total_budget || 1;
  const total = summary.total_budget || docTotal;
  const metaAlloc = ((pa.meta || 0) / docTotal) * total;
  const googleAlloc = ((pa.google || 0) / docTotal) * total;

  return (
    <div data-testid="budget-command-center" className="rounded-xl bg-[#1E293B] border border-white/10 p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Gauge className="w-5 h-5 text-[#38BDF8]" />
          <h2 className="font-display text-lg font-semibold">Budget Command Center</h2>
        </div>
        <span className="text-xs text-slate-400">
          Day {summary.days_elapsed} of {summary.days_in_month}
        </span>
      </div>

      {/* Pacing */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-lg bg-[#0F172A] border border-white/10 p-4">
          <div className="flex items-center gap-1.5 text-slate-400 text-xs uppercase tracking-wider mb-2">
            <Flame className="w-3.5 h-3.5" /> Burn Rate
          </div>
          <p className="font-display text-xl font-bold" style={{ color: pacingColor }}>{burnLabel}</p>
          <p className="text-xs text-slate-500 mt-1">
            {summary.spend_pct}% spent vs {summary.elapsed_pct}% elapsed
          </p>
        </div>
        <div className="rounded-lg bg-[#0F172A] border border-white/10 p-4">
          <div className="flex items-center gap-1.5 text-slate-400 text-xs uppercase tracking-wider mb-2">
            <CalendarClock className="w-3.5 h-3.5" /> Projected EOM
          </div>
          <p className="font-display text-xl font-bold tabular">{formatCompactINR(summary.projected_spend)}</p>
          <p className="text-xs mt-1" style={{ color: summary.projected_spend > summary.total_budget ? "#EF4444" : "#10B981" }}>
            {summary.projected_spend > summary.total_budget
              ? `${formatCompactINR(summary.projected_spend - summary.total_budget)} over budget`
              : "Within budget"}
          </p>
        </div>
        <div className="rounded-lg bg-[#0F172A] border border-white/10 p-4">
          <div className="flex items-center gap-1.5 text-slate-400 text-xs uppercase tracking-wider mb-2">
            <Gauge className="w-3.5 h-3.5" /> Remaining
          </div>
          <p className="font-display text-xl font-bold tabular text-[#10B981]">{formatCompactINR(summary.remaining)}</p>
          <p className="text-xs text-slate-500 mt-1">of {formatCompactINR(summary.total_budget)}</p>
        </div>
      </div>

      {/* Pacing dual bar */}
      <div className="space-y-4 mb-6">
        <Bar label="Spend Progress" value={summary.spend_pct} max={100} color={pacingColor}
          sub={`${summary.spend_pct}%`} />
        <Bar label="Month Elapsed" value={summary.elapsed_pct} max={100} color="#334155"
          sub={`${summary.elapsed_pct}%`} />
      </div>

      {/* Platform allocation */}
      <div className="pt-5 border-t border-white/10">
        <p className="text-xs tracking-[0.1em] uppercase text-slate-400 mb-3">Allocation by Platform</p>
        <div className="space-y-3">
          <Bar label="Meta Ads" value={metaAlloc} max={total} color="#38BDF8"
            sub={formatCompactINR(metaAlloc)} />
          <Bar label="Google Ads" value={googleAlloc} max={total} color="#818CF8"
            sub={formatCompactINR(googleAlloc)} />
        </div>
      </div>
    </div>
  );
}
